/**
 * How emotes and emoji look (data/emotes has what they are). An emote is a run of rig poses played
 * at a rate (a still emote is one pose); an emoji is a small icon in a parchment speech bubble over
 * the head. Icons are painted in code like the rest of the art — realistic pixel art: hard pixels,
 * each shape lit as a volume from the upper right with cool shadows and a dark outline.
 */
import { DANCE_POSES, danceSeconds } from './dances';
import { LIFE_ART } from './lifeArt';
import { canvas, ctx2d } from './pixel';
import { FRAMES, motionPhase, type Pose } from './sprites';

type G = CanvasRenderingContext2D;

/** An emote's frames (pose and phase) and how many play per second. */
export interface EmoteArt {
  frames: readonly (readonly [Pose, number])[];
  fps: number;
}

/** A looping emote: `FRAMES` frames of `pose`, the whole loop taking `seconds`. */
const loop = (pose: Pose, seconds: number): EmoteArt => ({
  frames: Array.from({ length: FRAMES }, (_, i) => [pose, i] as const),
  fps: FRAMES / seconds,
});

/** A two-beat loop of `FRAMES` frames: key pose phase 0 for `share` of `seconds`, then phase 1. */
const twoBeat = (pose: Pose, share: number, seconds: number): EmoteArt => ({
  frames: Array.from({ length: FRAMES }, (_, i) => [pose, i < Math.round(FRAMES * share) ? 0 : 1] as const),
  fps: FRAMES / seconds,
});

export const EMOTE_ART: Readonly<Record<string, EmoteArt>> = {
  salute: { frames: [['bow', 0]], fps: 1 },
  // The bout's loser: down on the knees, breathing slow (receive.ts kneel keys).
  defeat: { frames: [['rcvKneelHold', 0], ['rcvKneelBreath', 0], ['rcvKneelHold', 0]], fps: 0.8 },
  akimbo: { frames: [['akimbo', 0]], fps: 1 },
  fold: { frames: [['fold', 0]], fps: 1 },
  think: { frames: [['think', 0]], fps: 1 },
  point: { frames: [['point', 0]], fps: 1 },
  lounge: { frames: [['lounge', 0]], fps: 1 },
  guard: { frames: [['lowGuard', 0]], fps: 1 },
  wave: loop('wave', 0.4),
  cheer: loop('cheer', 0.5),
  clap: loop('clap', 1 / 3),
  laugh: loop('laugh', 0.5),
  cry: loop('cry', 0.4),
  dance: loop('dance', 1),
  kowtow: loop('kowtow', 1.6),
  // A sword form: guard, coil, cut, carry through, thrust, overhead, and back to guard.
  kata: {
    frames: Array.from({ length: FRAMES }, (_, i) => ['motion', motionPhase('kata', i)] as const),
    // Its eight key positions at the pace they were held one by one (three a second).
    fps: (FRAMES * 3) / 8,
  },
  // ท่าเต้น: each round as long as the shared data says (render/dances.ts has the key positions).
  ...Object.fromEntries(DANCE_POSES.map((p) => [p, loop(p, danceSeconds(p))])),
  // Professions at work (shared professions.ts `workEmote`): the townsfolk's work poses, timed as
  // `lifeFrame` plays them — the hoe up slowly and down hard, the broom to and fro.
  'work-hoe': twoBeat('hoe', 0.95 / 1.5, 1.5),
  'work-sweep': twoBeat('sweep', 0.5, 1.1),
  'work-fish': { frames: [['fish', 0]], fps: 1 },
  'work-mend': { frames: [['mend', 0]], fps: 1 },
  // The townsfolk's daily life (shared data/npcLife.ts, render/lifeArt.ts).
  ...LIFE_ART,
};

/** The emote's frame at `t` seconds into it. */
export function emoteFrame(id: string, t: number): readonly [Pose, number] | null {
  const art = EMOTE_ART[id];
  if (!art) return null;
  return art.frames[Math.floor(t * art.fps) % art.frames.length]!;
}

// ── emoji icons ──

/** Icon size in pixels. */
export const EMOJI_SIZE = 15;

/** Tones of a lit volume: highlight, light, mid, shadow, outline. */
type Tones = readonly [string, string, string, string, string];
const SUN: Tones = ['#fff4c0', '#f8d360', '#e2a83a', '#b06e2a', '#4a2a14'];
const RAGE: Tones = ['#ffd8c0', '#f49060', '#d8582e', '#9a2e1e', '#3e1410'];
const HEART: Tones = ['#ffd0d4', '#f05a64', '#c8303e', '#82182a', '#3a0a12'];
const BONE: Tones = ['#ffffff', '#ece6d8', '#c8bea8', '#8e8270', '#2e2620'];
const INGOT: Tones = ['#fff6c8', '#f4d05a', '#d8a030', '#94601c', '#3e2408'];
const SKIN: Tones = ['#ffe6cc', '#f2c49a', '#d89a6e', '#a8664a', '#4a2a1e'];
const INK = '#2a1a14';
const TEAR = ['#e8f8ff', '#6ac0f0', '#2a78c0'] as const;

/**
 * Paints the pixels for which `inside` holds, each lit by where it lies from (cx, cy) toward the
 * upper right, with an outline where the shape meets the outside.
 */
function volume(
  g: G,
  inside: (x: number, y: number) => boolean,
  tones: Tones,
  cx: number,
  cy: number,
  r: number,
): void {
  for (let y = 0; y < EMOJI_SIZE; y++)
    for (let x = 0; x < EMOJI_SIZE; x++) {
      if (!inside(x, y)) continue;
      const edge = !inside(x - 1, y) || !inside(x + 1, y) || !inside(x, y - 1) || !inside(x, y + 1);
      const lit = ((x - cx) * 0.6 - (y - cy) * 0.8) / r;
      g.fillStyle = edge
        ? tones[4]
        : lit > 0.62
          ? tones[0]
          : lit > 0.1
            ? tones[1]
            : lit > -0.45
              ? tones[2]
              : tones[3];
      g.fillRect(x, y, 1, 1);
    }
}

/** Pixels from rows of characters ('.' left empty), each character a colour of `pal`. */
function grid(g: G, rows: readonly string[], pal: Readonly<Record<string, string>>, ox = 0, oy = 0): void {
  rows.forEach((row, y) =>
    [...row].forEach((ch, x) => {
      const c = pal[ch];
      if (!c) return;
      g.fillStyle = c;
      g.fillRect(ox + x, oy + y, 1, 1);
    }),
  );
}

const C = 7; // centre of the icon
const round = (r: number) => (x: number, y: number) => (x - C) ** 2 + (y - C) ** 2 <= r * r + 0.5;

/** A face: a lit ball, with its features over it. */
function face(g: G, tones: Tones = SUN): void {
  volume(g, round(6.6), tones, C, C, 6.6);
}

const dots = (g: G, col: string, pts: readonly (readonly [number, number])[]) => {
  g.fillStyle = col;
  for (const [x, y] of pts) g.fillRect(x, y, 1, 1);
};

const ICONS: Record<string, (g: G) => void> = {
  smile(g) {
    face(g);
    dots(g, INK, [
      [5, 5],
      [5, 6],
      [9, 5],
      [9, 6],
      [4, 9],
      [5, 10],
      [6, 10],
      [7, 10],
      [8, 10],
      [9, 10],
      [10, 9],
    ]);
    dots(g, '#f08a6a', [
      [3, 8],
      [11, 8],
    ]);
  },
  laugh(g) {
    face(g);
    // Eyes squeezed shut (^ ^), mouth wide open with the tongue showing.
    dots(g, INK, [
      [4, 6],
      [5, 5],
      [6, 6],
      [8, 6],
      [9, 5],
      [10, 6],
    ]);
    grid(g, ['kkkkkkk', 'kwwwwwk', '.krrrk.', '..kkk..'], { k: INK, w: '#ffffff', r: '#d8484a' }, 4, 8);
  },
  love(g) {
    // A heart: (x² + y² − 1)³ − x²y³ ≤ 0, lit like a glossy volume.
    const inside = (x: number, y: number) => {
      const u = (x - 7) / 6.2;
      const v = -(y - 7.6) / 6.2;
      return (u * u + v * v - 1) ** 3 - u * u * v * v * v <= 0;
    };
    volume(g, inside, HEART, 7, 6, 6.5);
    dots(g, '#ffffff', [
      [9, 4],
      [10, 5],
    ]);
  },
  cry(g) {
    face(g);
    dots(g, INK, [
      [4, 6],
      [5, 6],
      [6, 6],
      [8, 6],
      [9, 6],
      [10, 6],
      [5, 10],
      [6, 9],
      [7, 9],
      [8, 9],
      [9, 10],
    ]);
    // Tears streaming down both cheeks.
    for (const x of [4, 10]) {
      dots(g, TEAR[0], [[x, 7]]);
      dots(g, TEAR[1], [
        [x, 8],
        [x, 9],
      ]);
      dots(g, TEAR[2], [
        [x, 10],
        [x, 11],
      ]);
    }
  },
  angry(g) {
    face(g, RAGE);
    // Brows slammed down, teeth gritted, and a throbbing vein (💢) at the temple.
    dots(g, INK, [
      [4, 4],
      [5, 5],
      [6, 6],
      [10, 4],
      [9, 5],
      [8, 6],
      [5, 7],
      [9, 7],
    ]);
    grid(g, ['kkkkk', 'kwkwk', 'kkkkk'], { k: INK, w: '#fff4e8' }, 5, 9);
    grid(g, ['r.r', '...', 'r.r'], { r: '#ff2a2a' }, 11, 1);
    dots(g, '#ffd0c0', [[12, 2]]);
  },
  shock(g) {
    face(g);
    dots(g, '#ffffff', [
      [4, 5],
      [5, 5],
      [9, 5],
      [10, 5],
    ]);
    dots(g, INK, [
      [4, 6],
      [5, 6],
      [9, 6],
      [10, 6],
    ]);
    grid(g, ['.kk.', 'kddk', 'kddk', '.kk.'], { k: INK, d: '#6a1a1a' }, 5, 8);
  },
  sweat(g) {
    face(g);
    dots(g, INK, [
      [4, 6],
      [5, 6],
      [9, 6],
      [10, 6],
      [5, 10],
      [6, 10],
      [7, 10],
      [8, 10],
      [9, 10],
    ]);
    // A big drop down the side of the head.
    grid(g, ['.a.', 'aba', 'bbc', '.c.'], { a: TEAR[0], b: TEAR[1], c: TEAR[2] }, 11, 2);
  },
  sleep(g) {
    face(g);
    dots(g, INK, [
      [4, 7],
      [5, 7],
      [6, 7],
      [8, 7],
      [9, 7],
      [10, 7],
      [6, 10],
      [7, 10],
      [8, 10],
    ]);
    // A bubble from the nose, and the z's drifting up.
    dots(g, TEAR[0], [
      [10, 9],
      [11, 9],
      [10, 10],
    ]);
    dots(g, TEAR[1], [[11, 10]]);
    grid(g, ['zzz', '.z.', 'zzz'], { z: '#3a5aa8' }, 11, 0);
  },
  question(g) {
    grid(
      g,
      [
        '..kkkkk..',
        '.kaabbbk.',
        'kaab.kbbk',
        'kkk..kbbk',
        '....kbbk.',
        '...kbbk..',
        '...kbck..',
        '...kkkk..',
        '.........',
        '...kbbk..',
        '...kcck..',
        '...kkkk..',
      ],
      { k: '#3a2410', a: '#fff0b0', b: '#f0c040', c: '#b07a20' },
      3,
      1,
    );
  },
  exclaim(g) {
    grid(
      g,
      ['kkkk', 'kabk', 'kabk', 'kbbk', 'kbck', 'kbck', '.kk.', '....', 'kkkk', 'kbbk', 'kcck', 'kkkk'],
      { k: '#3a0a0e', a: '#ffc0b0', b: '#f04030', c: '#a01818' },
      5,
      1,
    );
  },
  cool(g) {
    face(g);
    // Dark glasses with a glint, and a smirk.
    grid(
      g,
      ['kkkkkkkkkkk', '.kgwk.kggk.', '.kggk.kggk.', '..kk...kk..'],
      { k: '#141018', g: '#2e2a3a', w: '#b8c8e8' },
      2,
      5,
    );
    dots(g, INK, [
      [6, 10],
      [7, 10],
      [8, 10],
      [9, 9],
    ]);
  },
  thumb(g) {
    grid(
      g,
      [
        '.....kk........',
        '....kabk.......',
        '....kabk.......',
        '...kaabk.......',
        '.kkkaabkkkkk...',
        'kaaabbbbbbbck..',
        'kabbbbbbbbbck..',
        'kkbbbbbbbbbck..',
        'kabbbbbbbbcck..',
        'kkbbbbbbbbcck..',
        'kabbbbbbbccck..',
        '.kkkkkkkkkkk...',
      ],
      { k: SKIN[4], a: SKIN[0], b: SKIN[1], c: SKIN[2] },
      0,
      1,
    );
    // The sleeve.
    grid(g, ['rr', 'rr', 'ss', 'ss', 'ss', 'ss', 'ss'], { r: '#e8c27e', s: '#8a1a24' }, 12, 5);
  },
  fist(g) {
    grid(
      g,
      [
        '..kkkkkkkk...',
        '.kaabkabbbk..',
        'kaabbkbbbbck.',
        'kabbbkbbbcck.',
        'kkkkkkkkkkck.',
        'kaabbbbbbbck.',
        'kabbbbbbbcck.',
        'kabbbbbbccck.',
        '.kcccccccck..',
        '..kkkkkkkk...',
      ],
      { k: SKIN[4], a: SKIN[0], b: SKIN[1], c: SKIN[2] },
      1,
      1,
    );
    // Speed lines of a punch.
    dots(g, '#f8d360', [
      [13, 3],
      [14, 3],
      [13, 6],
      [14, 6],
      [13, 9],
      [14, 9],
    ]);
  },
  note(g) {
    // Two quavers joined by a beam.
    grid(
      g,
      [
        '....kkkkkkkk',
        '....kaaaaaak',
        '....kbkkkkbk',
        '....kbk..kbk',
        '....kbk..kbk',
        '....kbk..kbk',
        '....kbk..kbk',
        '.kkkkbk.kkbk',
        'kaabbbkkabbk',
        'kabbbck.kbck',
        'kbbccck.kcck',
        '.kkkkk..kkk.',
      ],
      { k: '#1a1430', a: '#c8b8ff', b: '#7a62d8', c: '#4a3a98' },
      1,
      1,
    );
  },
  skull(g) {
    volume(
      g,
      (x, y) => (x - 7) ** 2 / 36 + (y - 6) ** 2 / 30 <= 1 || (x >= 4 && x <= 10 && y >= 9 && y <= 13),
      BONE,
      7,
      6,
      6.5,
    );
    grid(g, ['kk.kk', 'kk.kk', '..k..'], { k: '#1a1410' }, 5, 6);
    dots(g, '#1a1410', [
      [5, 12],
      [7, 12],
      [9, 12],
    ]);
  },
  gold(g) {
    // A gold ingot (元寶): the boat-shaped base and the rounded crown, with a glint.
    volume(g, (x, y) => (x - 7) ** 2 / 12 + (y - 6) ** 2 / 9 <= 1, INGOT, 7, 6, 4);
    volume(
      g,
      (x, y) =>
        y >= 7 &&
        y <= 12 &&
        Math.abs(x - 7) <= 7 - Math.max(0, y - 9) * 1.4 &&
        !(y === 7 && Math.abs(x - 7) < 4),
      INGOT,
      7,
      9,
      7,
    );
    dots(g, '#ffffff', [
      [8, 4],
      [12, 8],
    ]);
  },
};

const iconCache = new Map<string, HTMLCanvasElement>();

/** The icon for an emoji, painted once. */
export function emojiIcon(id: string): HTMLCanvasElement {
  let c = iconCache.get(id);
  if (!c) {
    c = canvas(EMOJI_SIZE, EMOJI_SIZE);
    const g = ctx2d(c);
    (ICONS[id] ?? ICONS.question!)(g);
    iconCache.set(id, c);
  }
  return c;
}

/**
 * The emoji over a head whose top is at (x, y): popped up in a parchment bubble with its tail
 * pointing down, bobbing, and fading as it goes. `age` is seconds since it appeared, `left`
 * seconds still to show.
 */
export function drawEmojiBubble(g: G, id: string, x: number, y: number, age: number, left: number): void {
  const pop = Math.min(1, age / 0.14);
  // Overshoot a little as it pops, then settle.
  const k = pop < 1 ? 0.5 + pop * 0.62 : 1 + Math.max(0, 0.08 - (age - 0.14)) * 0.6;
  const w = EMOJI_SIZE + 6;
  const h = EMOJI_SIZE + 6;
  const bob = Math.round(Math.sin(age * 4) * 1);
  g.save();
  g.imageSmoothingEnabled = false;
  g.globalAlpha *= Math.min(1, left / 0.35);
  g.translate(Math.round(x), Math.round(y - 6 + bob));
  g.scale(k, k);
  const L = -Math.round(w / 2);
  const T = -h - 3;
  // Bubble: dark outline, parchment body lit from the top, a shadowed lower edge, and the tail.
  g.fillStyle = '#2a1c14';
  g.fillRect(L + 1, T, w - 2, h);
  g.fillRect(L, T + 1, w, h - 2);
  g.fillRect(-2, T + h, 4, 1);
  g.fillRect(-1, T + h + 1, 2, 1);
  g.fillStyle = '#f2e6cc';
  g.fillRect(L + 1, T + 1, w - 2, h - 2);
  g.fillRect(-1, T + h - 1, 2, 1);
  g.fillStyle = '#fffaf0';
  g.fillRect(L + 2, T + 1, w - 5, 1);
  g.fillRect(w / 2 + L - 2, T + 2, 1, 2);
  g.fillStyle = '#cdb894';
  g.fillRect(L + 1, T + h - 2, w - 2, 1);
  g.fillRect(-1, T + h, 2, 1);
  g.drawImage(emojiIcon(id), L + 3, T + 3);
  g.restore();
}
